/**
 * MultiSelect — the one multi-select in the app (plan
 * docs/plans/2026-10-16-shared-multiselect.md). Closed it reads as a
 * one-line summary ("Discounts, Orders +2"); open it shows the picked values as
 * chips, a search field and a checkbox list with a "N selected · Done" footer.
 *
 * Built on Mantine `Combobox`, not on Mantine's `MultiSelect`: that one wires
 * pills and search into a single `Pill.Group` and has no footer slot, so the
 * summary and the footer could only be forged on top of it.
 *
 * Commit timing is Mantine's: every toggle calls `onChange` at once. **Done**
 * only closes; there is nothing left to apply.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from "react";
import {
  Badge,
  Button,
  CheckboxIndicator,
  CloseButton,
  Combobox,
  Input,
  Loader,
  Pill,
  ScrollArea,
  Text,
  UnstyledButton,
  useCombobox,
} from "@mantine/core";
import type { ComboboxProps, MantineSize } from "@mantine/core";
import { IconAlertCircle, IconChevronDown, IconChevronUp, IconRefresh } from "@tabler/icons-react";
import classes from "./MultiSelect.module.css";

export interface MultiSelectItem {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface MultiSelectGroup {
  group: string;
  items: ReadonlyArray<string | MultiSelectItem>;
}

export type MultiSelectData = ReadonlyArray<string | MultiSelectItem | MultiSelectGroup>;

export interface MultiSelectProps {
  data: MultiSelectData;
  value: string[];
  onChange: (value: string[]) => void;
  label?: ReactNode;
  description?: ReactNode;
  /** A string/node shows under the field; `true` only marks the field red. */
  error?: ReactNode;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /** Values stay visible (the list still opens) but nothing can change. */
  readOnly?: boolean;
  size?: MantineSize;
  w?: CSSProperties["width"];
  style?: CSSProperties;
  className?: string;
  leftSection?: ReactNode;
  maxValues?: number;
  searchable?: boolean;
  clearable?: boolean;
  nothingFoundMessage?: ReactNode;
  searchValue?: string;
  onSearchChange?: (search: string) => void;
  maxDropdownHeight?: number;
  comboboxProps?: ComboboxProps;
  /** Plural, lower case ("metrics"): drives the default placeholder and messages. */
  noun?: string;
  /** The options' initial load is in flight. Never set this for a search in flight. */
  loading?: boolean;
  /** A search the call site runs itself is in flight (controlled search). */
  searching?: boolean;
  /** The options could not be loaded; pair with `onRetry`. */
  loadError?: boolean;
  onRetry?: () => void;
  /** `"all"`: an empty value means every option, and renders every box checked. */
  emptyMeans?: "none" | "all";
  /** Where the open chips go. `"dropdown"` for fields inside a horizontal toolbar. */
  chipsPlacement?: "field" | "dropdown";
  id?: string;
  "aria-label"?: string;
  "data-testid"?: string;
}

interface Section {
  group: string | null;
  items: MultiSelectItem[];
}

function toItem(entry: string | MultiSelectItem): MultiSelectItem {
  return typeof entry === "string" ? { value: entry, label: entry } : entry;
}

function toSections(data: MultiSelectData): Section[] {
  const sections: Section[] = [];
  let loose: Section | null = null;
  for (const entry of data) {
    if (typeof entry !== "string" && "group" in entry) {
      loose = null;
      sections.push({ group: entry.group, items: entry.items.map(toItem) });
    } else {
      if (!loose) {
        loose = { group: null, items: [] };
        sections.push(loose);
      }
      loose.items.push(toItem(entry));
    }
  }
  return sections;
}

const isPrintable = (event: KeyboardEvent) =>
  event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey;

export function MultiSelect({
  data,
  value,
  onChange,
  label,
  description,
  error,
  placeholder,
  required,
  disabled = false,
  readOnly = false,
  size = "sm",
  w,
  style,
  className,
  leftSection,
  maxValues,
  searchable = false,
  clearable = false,
  nothingFoundMessage,
  searchValue,
  onSearchChange,
  maxDropdownHeight = 260,
  comboboxProps,
  noun,
  loading = false,
  searching = false,
  loadError = false,
  onRetry,
  emptyMeans = "none",
  chipsPlacement = "field",
  id,
  "aria-label": ariaLabel,
  "data-testid": testId,
}: MultiSelectProps) {
  const autoId = useId();
  const inputId = id ?? `ms-${autoId}`;

  const [internalSearch, setInternalSearch] = useState("");
  const search = searchValue ?? internalSearch;
  const setSearch = (next: string) => {
    if (searchValue === undefined) setInternalSearch(next);
    onSearchChange?.(next);
  };

  const store = useCombobox({
    onDropdownClose: () => {
      store.resetSelectedOption();
      if (search) setSearch("");
    },
  });
  const opened = store.dropdownOpened;

  const sections = useMemo(() => toSections(data), [data]);
  const allItems = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const byValue = useMemo(() => new Map(allItems.map((i) => [i.value, i])), [allItems]);

  // A picked value can leave `data` (a server search, a top-N value list, a
  // deleted source). Keep the last label we saw so the summary never shows a
  // bare id where it showed a name a moment ago; fall back to the raw value.
  const retained = useRef(new Map<string, string>());
  for (const v of value) {
    const item = byValue.get(v);
    if (item) retained.current.set(v, item.label);
  }
  const labelOf = (v: string) => byValue.get(v)?.label ?? retained.current.get(v) ?? v;

  const blocked = disabled || readOnly || loading;
  const allMode = emptyMeans === "all";
  const isChecked = (v: string) => (allMode && value.length === 0) || value.includes(v);
  const atCap = maxValues !== undefined && value.length >= maxValues;

  const toggle = (v: string) => {
    if (blocked) return;
    let next: string[];
    if (allMode && value.length === 0) {
      next = allItems.map((i) => i.value).filter((x) => x !== v);
    } else if (value.includes(v)) {
      next = value.filter((x) => x !== v);
    } else {
      if (atCap) return;
      next = [...value, v];
      if (allMode && allItems.every((i) => next.includes(i.value))) next = [];
    }
    onChange(next);
    if (search) setSearch("");
  };

  const remove = (v: string) => {
    if (blocked) return;
    onChange(value.filter((x) => x !== v));
  };

  const query = search.trim().toLowerCase();
  const visibleSections = sections
    .map((s) => ({
      ...s,
      items:
        searchable && query && !readOnly
          ? s.items.filter((i) => i.label.toLowerCase().includes(query))
          : readOnly
            ? s.items.filter((i) => value.includes(i.value))
            : s.items,
    }))
    .filter((s) => s.items.length > 0);

  // ── Copy ──────────────────────────────────────────────────────────────────
  const thing = noun ?? "options";
  const placeholderText = placeholder ?? (noun ? `Select ${noun}` : undefined);
  const shownCount = size === "xs" ? 1 : 2;
  const shown = value.slice(0, shownCount).map(labelOf);
  const overflow = value.length - shown.length;
  const summaryText = shown.join(", ");
  const fullSummary =
    value.length === 0
      ? ""
      : overflow > 0
        ? `${shown.join(", ")} and ${overflow} more`
        : summaryText;
  const countText =
    maxValues !== undefined ? `${value.length} of ${maxValues} selected` : `${value.length} selected`;

  let fieldValue: string;
  let fieldPlaceholder: string | undefined;
  if (loading && value.length === 0) {
    fieldValue = "";
    fieldPlaceholder = `Loading ${thing}…`;
  } else if (opened && searchable && !readOnly) {
    fieldValue = search;
    fieldPlaceholder = noun ? `Search ${noun}…` : "Search…";
  } else {
    fieldValue = summaryText;
    fieldPlaceholder = placeholderText;
  }

  // ── Events ────────────────────────────────────────────────────────────────
  const open = () => {
    if (disabled || loading) return;
    store.openDropdown();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled || loading) return;
    if (loadError && event.key === "Enter" && onRetry) {
      event.preventDefault();
      onRetry();
      return;
    }
    if (!opened) {
      if (event.key === "Enter" || (event.key === " " && !searchable)) {
        event.preventDefault();
        open();
        return;
      }
      if (searchable && !readOnly && isPrintable(event)) {
        event.preventDefault();
        open();
        setSearch(event.key);
        return;
      }
      return;
    }
    if (event.key === " " && (!searchable || readOnly)) {
      event.preventDefault();
      if (store.getSelectedOptionIndex() !== -1) store.clickSelectedOption();
      return;
    }
    if (event.key === "Backspace" && searchable && search === "" && value.length > 0) {
      event.preventDefault();
      remove(value[value.length - 1]);
    }
  };

  // Typing a query highlights the first match, so Enter picks it. Keyed on the
  // query only: `store` is a new object every render, and re-running this on
  // each arrow key would reset the highlight it just moved.
  useEffect(() => {
    if (opened && searchable && search) store.selectFirstOption();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Footer, chips and retry never take focus: the field keeps it, so blur-close,
  // Esc (the target carries `data-mantine-stop-propagation`) and the keyboard
  // contract stay in one place.
  const keepFocus = (event: MouseEvent) => event.preventDefault();

  // ── Pieces ────────────────────────────────────────────────────────────────
  const chips = (
    <div className={classes.chips} data-placement={chipsPlacement}>
      {value.length === 0 ? (
        <Text size="xs" c="dimmed">
          None selected
        </Text>
      ) : (
        value.map((v) => (
          <Pill
            key={v}
            size={size}
            withRemoveButton={!blocked}
            onRemove={() => remove(v)}
            removeButtonProps={{
              tabIndex: -1,
              // Pill hides its × from assistive tech by default; ours carries a
              // name, and Backspace is the keyboard path.
              "aria-hidden": false,
              "aria-label": `Remove ${labelOf(v)}`,
            }}
            className={classes.chip}
          >
            {labelOf(v)}
          </Pill>
        ))
      )}
    </div>
  );

  const showChips = opened && !readOnly;

  const right: ReactNode[] = [];
  let rightWidth = 30;
  if (loading) {
    right.push(<Loader key="loading" size={14} aria-label={`Loading ${thing}`} />);
  } else if (loadError && onRetry && !opened) {
    right.push(
      <UnstyledButton
        key="retry"
        className={classes.retry}
        onMouseDown={keepFocus}
        onClick={onRetry}
        aria-label="Try again"
      >
        <IconRefresh size={14} />
      </UnstyledButton>,
    );
  }
  if (!loading) {
    if (overflow > 0 && !(opened && searchable && !readOnly)) {
      right.push(
        <Badge key="more" variant="light" color="gray" radius="sm" className={classes.more} aria-hidden>
          +{overflow}
        </Badge>,
      );
      rightWidth += 36;
    }
    const canClear = clearable && !blocked && (opened && searchable ? search.length > 0 : value.length > 0);
    if (canClear) {
      right.push(
        <CloseButton
          key="clear"
          size="sm"
          tabIndex={-1}
          onMouseDown={keepFocus}
          aria-label={opened && searchable ? "Clear search" : "Clear selection"}
          onClick={() => (opened && searchable ? setSearch("") : onChange([]))}
        />,
        <span key="divider" className={classes.divider} aria-hidden />,
      );
      rightWidth += 34;
    }
    if (loadError && onRetry && !opened) rightWidth += 24;
    right.push(
      <span
        key="chevron"
        className={classes.chevron}
        aria-hidden
        onMouseDown={keepFocus}
        onClick={() => (opened ? store.closeDropdown() : open())}
      >
        {opened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
      </span>,
    );
  }

  const errorNode =
    error && typeof error !== "boolean" ? (
      <span className={classes.error}>
        <IconAlertCircle size={14} aria-hidden />
        <span>{error}</span>
      </span>
    ) : (
      error
    );

  const emptyMessage = searching
    ? "Searching…"
    : nothingFoundMessage ??
      (query
        ? noun
          ? `No ${noun} found for “${search.trim()}”.`
          : `Nothing found for “${search.trim()}”.`
        : noun
          ? `No ${noun} available.`
          : "Nothing to choose from.");

  return (
    <Combobox
      store={store}
      onOptionSubmit={toggle}
      withinPortal
      {...comboboxProps}
      classNames={{ dropdown: "verity-dropdown", ...comboboxProps?.classNames }}
    >
      {/* The selection summary rides in the description slot: Input spreads
          the wrapper's aria-describedby (error + description) AFTER its own
          props, so a summary id of ours would be dropped. Visually it stays
          hidden; a field without a visible description gets no extra gap. */}
      <Input.Wrapper
        id={inputId}
        label={label}
        description={
          description || value.length > 0 ? (
            <>
              {description}
              <span className={classes.visuallyHidden}>
                {description ? " " : ""}
                {fullSummary}
              </span>
            </>
          ) : undefined
        }
        descriptionProps={description ? undefined : { className: classes.summaryOnly }}
        error={errorNode}
        required={required}
        size={size}
        w={w}
        style={style}
        className={className}
      >
        {/* The chips sit inside the dropdown's anchor: a list that flips
            upward (a field low in a modal) then lands above them, not on them. */}
        <Combobox.DropdownTarget>
          <div className={classes.target}>
            {showChips && chipsPlacement === "field" && chips}
            <Combobox.EventsTarget>
              <Input
                id={inputId}
                size={size}
                value={fieldValue}
                placeholder={fieldPlaceholder}
                onChange={(event) => setSearch(event.currentTarget.value)}
                onKeyDown={onKeyDown}
                // Like Mantine: clicking the field only opens. The chevron toggles.
                onClick={open}
                onBlur={() => store.closeDropdown()}
                readOnly={!(opened && searchable && !readOnly)}
                disabled={disabled}
                error={!!error}
                leftSection={leftSection}
                rightSection={right.length > 0 ? <div className={classes.right}>{right}</div> : null}
                rightSectionWidth={rightWidth}
                rightSectionPointerEvents="all"
                classNames={{ input: `verity-input ${classes.input}` }}
                aria-label={ariaLabel}
                title={value.length > 0 ? value.map(labelOf).join(", ") : undefined}
                data-testid={testId}
                data-readonly={readOnly || undefined}
              />
            </Combobox.EventsTarget>
          </div>
        </Combobox.DropdownTarget>
        {/* Mantine's MultiSelect renders one too: the values joined by "," for
            a surrounding <form>, and what some tests read the value from. */}
        <Combobox.HiddenInput value={value} disabled={disabled} />
        <span className={classes.visuallyHidden} aria-live="polite">
          {opened && !readOnly ? `${value.length} ${noun ?? ""} selected`.replace(/\s+/g, " ") : ""}
        </span>
      </Input.Wrapper>

      <Combobox.Dropdown>
        {showChips && chipsPlacement === "dropdown" && <Combobox.Header>{chips}</Combobox.Header>}
        {loadError ? (
          <div className={classes.message}>
            <Text size="sm" c="dimmed">
              Couldn’t load {thing}.
            </Text>
            {onRetry && (
              <Button
                variant="subtle"
                size="compact-sm"
                leftSection={<IconRefresh size={14} />}
                tabIndex={-1}
                onMouseDown={keepFocus}
                onClick={onRetry}
              >
                Try again
              </Button>
            )}
          </div>
        ) : (
          <Combobox.Options aria-multiselectable="true">
            <ScrollArea.Autosize mah={maxDropdownHeight} type="scroll">
              {visibleSections.length === 0 ? (
                <Combobox.Empty>{emptyMessage}</Combobox.Empty>
              ) : (
                visibleSections.map((section, index) => {
                  const options = section.items.map((item) => {
                    const checked = isChecked(item.value);
                    const optionDisabled =
                      readOnly || item.disabled || (!checked && atCap && !allMode);
                    return (
                      <Combobox.Option
                        key={item.value}
                        value={item.value}
                        disabled={optionDisabled}
                        aria-checked={checked}
                        mod={{ checked }}
                        className={classes.option}
                      >
                        <CheckboxIndicator
                          checked={checked}
                          disabled={optionDisabled && !readOnly}
                          size="sm"
                          aria-hidden
                        />
                        <span className={classes.optionLabel}>{item.label}</span>
                      </Combobox.Option>
                    );
                  });
                  return section.group ? (
                    <Combobox.Group key={`${section.group}-${index}`} label={section.group}>
                      {options}
                    </Combobox.Group>
                  ) : (
                    <div key={`loose-${index}`}>{options}</div>
                  );
                })
              )}
            </ScrollArea.Autosize>
          </Combobox.Options>
        )}
        {!readOnly && !loadError && (
          <Combobox.Footer className={classes.footer}>
            <Text size="sm" c="dimmed">
              {countText}
            </Text>
            <Button variant="subtle" size="compact-sm" tabIndex={-1} onClick={() => store.closeDropdown()}>
              Done
            </Button>
          </Combobox.Footer>
        )}
      </Combobox.Dropdown>
    </Combobox>
  );
}
